import { useCallback } from 'react'

import { useCommandBarContext } from '../context/CommandBarContext'

/**
 * Simple hook to programmatically open the command bar.
 *
 * Use this when you need to trigger the command bar from a button or other UI element.
 * This hook does NOT register any keyboard shortcuts.
 *
 * Returns a stable callback safe to use directly as an onClick handler —
 * it will not leak the React event object into the context state.
 *
 * @example
 * ```tsx
 * function MyButton() {
 *   const openCommandBar = useOpenCommandBar()
 *
 *   return (
 *     <Button onClick={openCommandBar}>
 *       Open Command Bar
 *     </Button>
 *   )
 * }
 * ```
 */
export function useOpenCommandBar(): (event?: {
  currentTarget: EventTarget
}) => void {
  const { openCommandBar } = useCommandBarContext()
  return useCallback(
    (event) => {
      const returnFocusTo =
        event?.currentTarget instanceof HTMLElement
          ? event.currentTarget
          : undefined
      openCommandBar('button_click', returnFocusTo)
    },
    [openCommandBar],
  )
}
